import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client/react';
import { getOperationAST } from 'graphql/utilities';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { IExtendedCmControlsRisk } from '@pages-clean/risk-assessment/constants';
import { Permissions } from '@contexts/user-context';
import { useAuthorization } from '@hooks/index';
import Constants from '@utils/constants';
import { ShowError, ShowSuccess } from '@utils/notification';
import { GET_CM2_PANEL } from '@queries/central-monitoring';
import {
    assignRisksToCmControlChart,
    assignRisksToCmControlChartVariables,
    getCmControlRisksInfo,
    getCmControlRisksInfoVariables,
} from '@queries/generated/risk-assessment.generated';
import { ASSIGN_CM_CONTROL_RISKS, GET_CM_CONTROL_RISKS_INFO } from '@queries/risk-assessment';

interface UnassignControlProps {
    onClose: () => void;
    onSubmit: (result: string) => void;
    risk?: IExtendedCmControlsRisk;
    panel: {
        panelRef?: string | null;
        associatedRisks?: string[] | null;
        name?: string | null;
    };
    removeAllRisks?: boolean;
    studyId?: string;
}

const UnassignCmControlRiskDialog = ({
    onClose,
    onSubmit,
    risk,
    panel,
    removeAllRisks,
    studyId,
}: UnassignControlProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.unassignCmControlRisk' });
    const { t: tCommon } = useTranslation('common');
    const user = useAuthorization();
    const canViewInternalRisks = user?.can?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.InternalRisks,
        permission: Permissions.Enabled,
    });

    const [executeAssignRisksToCmControl] = useMutation<
        assignRisksToCmControlChart,
        assignRisksToCmControlChartVariables
    >(ASSIGN_CM_CONTROL_RISKS);

    const { data: riskInfo, loading } = useQuery<getCmControlRisksInfo, getCmControlRisksInfoVariables>(
        GET_CM_CONTROL_RISKS_INFO,
        {
            variables: {
                studyId,
                riskIds: panel.associatedRisks,
            },
            skip: !panel.associatedRisks,
        },
    );

    const risksUserCanView = riskInfo?.getCmControlsAssociatedRisksInfo?.map((res) => res?.id) ?? [];
    const internalRisks = panel.associatedRisks?.filter((p) => !risksUserCanView.includes(p));
    const risksCount = canViewInternalRisks
        ? panel.associatedRisks?.length
        : (panel.associatedRisks?.length ?? 0) - (internalRisks?.length ?? 0);

    const handleSubmit = async () => {
        const operationName = getOperationAST(GET_CM2_PANEL)?.name?.value;

        const riskIds = removeAllRisks
            ? canViewInternalRisks
                ? []
                : internalRisks
            : (panel.associatedRisks ?? []).filter((id) => id !== risk?.id);

        try {
            const response = await executeAssignRisksToCmControl({
                variables: {
                    id: panel.panelRef,
                    riskIds,
                },
                refetchQueries: [operationName!],
            });

            if (!response?.data?.assignRisksToCmControlChart) {
                ShowError(tCommon('errorMessage'));
                return;
            }
            ShowSuccess(tCommon('actionsSuccessfulToast'));
            onSubmit('success');
        } catch {
            ShowError(tCommon('errorMessage'));
            onSubmit('failure');
        }
    };

    return (
        <ConfirmationDialog
            title={removeAllRisks ? t('altTitle') : t('title')}
            dataTestId="unassign-cm-control-confirmation-dialog"
            onClose={() => onClose()}
            onSubmit={risksCount && risksCount > 0 ? () => handleSubmit() : undefined}
            primaryBtnDisabled={loading}
            secondaryBtnLabel={risksCount && risksCount > 0 ? tCommon('cancel') : tCommon('close')}
        >
            <div>
                <InfoField
                    type="Info"
                    message={
                        risksCount && risksCount > 0 ? (
                            <div style={{ width: '438px', margin: '0 0' }}>
                                <div className="k-d-flex k-align-items-center">
                                    <div className="k-font-medium">{t('description')}</div>
                                </div>
                                <div>{panel?.name ?? ''}</div>
                                <br />
                                {removeAllRisks ? (
                                    <div>
                                        {t('warning', {
                                            risks: risksCount,
                                        })}
                                    </div>
                                ) : (
                                    <>
                                        <div className="k-font-medium">{t('riskStatement')}</div>
                                        <div>{risk?.statement?.slice(0, 600)}</div>
                                        {panel.associatedRisks?.length === 1 && (
                                            <>
                                                <br />
                                                <div>{t('altWarning')}</div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <div style={{ width: '438px', margin: '0 0' }}>{t('internalOnlyWarning')}</div>
                        )
                    }
                />
                {risksCount && risksCount > 0 ? (
                    <div style={{ textAlign: 'center', marginTop: '30px', marginBottom: '18px' }}>
                        {removeAllRisks ? t('altMessage') : t('message')}
                    </div>
                ) : (
                    ''
                )}
            </div>
        </ConfirmationDialog>
    );
};

export default UnassignCmControlRiskDialog;
